import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rideAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';

// Deliberately no default sort by price — same reasoning as
// TransportRequestDetailPage / design doc §11.
const OfferCard = ({ offer, onAccept, accepting }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-bold text-gray-800">{offer.provider?.name}</span>
      {offer.etaMinutes && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">🕐 {offer.etaMinutes} دقيقة</span>}
    </div>
    {offer.provider?.ratingCount > 0 && (
      <p className="text-xs text-amber-600 font-semibold mb-1">★ {offer.provider.ratingAvg} ({offer.provider.ratingCount} تقييم)</p>
    )}
    <p className="text-2xl font-black text-blue-700 mb-2">{offer.price} <span className="text-sm font-bold text-gray-400">ج.س</span></p>
    <div className="text-xs text-gray-500 space-y-1 mb-3">
      <p>السائق: {offer.driverName}</p>
      {offer.vehiclePlate && <p>رقم اللوحة: {offer.vehiclePlate}</p>}
    </div>
    <button onClick={() => onAccept(offer)} disabled={accepting}
      className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-bold py-2.5 rounded-xl transition">
      قبول هذا العرض
    </button>
  </div>
);

const RideRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  // silent=true for background polls — only the very first load should show
  // the full-page spinner; re-showing it on every 8s poll made the whole
  // page flash/flicker continuously while waiting for offers.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    rideAPI.getRequestById(id)
      .then((res) => setRequest(res.data.data))
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(() => load(true), 8000); // rides are time-sensitive — poll more often than cargo quotes
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load(true);
        interval = setInterval(() => load(true), 8000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  const handleAccept = async (offer) => {
    if (!window.confirm(`تأكيد قبول عرض ${offer.provider?.name} بمبلغ ${offer.price} ج.س؟`)) return;
    setAccepting(true);
    try {
      const res = await rideAPI.acceptOffer(id, offer.id);
      navigate(`/rides/orders/${res.data.data.id}`, { replace: true });
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر قبول العرض');
    } finally { setAccepting(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('هل تريد إلغاء طلب المشوار هذا؟')) return;
    try {
      await rideAPI.cancelRequest(id);
      navigate('/rides');
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر إلغاء الطلب');
    }
  };

  if (loading) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">جاري التحميل...</p></div>;
  if (!request) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">الطلب غير موجود</p></div>;

  const pendingOffers = (request.offers || []).filter((o) => o.status === 'pending');

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-gray-400">{request.requestNumber}</span>
            {request.status === 'open_for_offers' && (
              <button onClick={handleCancel} className="text-xs font-bold text-red-600 hover:underline">إلغاء الطلب</button>
            )}
          </div>
          <p className="font-bold text-gray-800">{request.fromLabel} ← {request.toLabel}</p>
        </div>

        <h2 className="font-bold text-gray-800 mb-3">
          {pendingOffers.length > 0 ? `🚕 العروض المقدَّمة (${pendingOffers.length})` : 'بانتظار عروض السائقين'}
        </h2>

        {pendingOffers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-gray-500">
            لم يصل أي عرض بعد — هذه الصفحة تتحدث تلقائياً
          </div>
        ) : (
          <div className="space-y-4">
            {pendingOffers.map((o) => <OfferCard key={o.id} offer={o} onAccept={handleAccept} accepting={accepting} />)}
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default RideRequestDetailPage;
